import {
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import React, { useContext } from 'react'

import { SLAClockContext } from '../../components/SLAChip'
import { durationParts } from '../../helpers/slaPolicies'
import {
  formatWhen,
  promisedWithin,
  slaPolicyLabel,
  slaTone,
  slaUrgency,
  Ticket
} from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import SectionCard from './SectionCard'
import When from './When'

interface SlaPanelProps {
  ticket: Ticket
  /** Injected by tests; production reads the shared clock. */
  now?: Date
}

const HEADING_ID = 'sla-panel-heading'

/**
 * The ticket's service level, as a table.
 *
 * This screen already showed the two deadlines, as one run-on line of text
 * with no indication of whether either was in trouble — the reader got the
 * numbers and had to do the arithmetic against the current time themselves.
 * SLAChip answers that, but by design it says nothing at all unless a ticket
 * is breached or close to it, so a healthy ticket showed deadlines and no
 * verdict. What is new here is the verdict, and a shape that survives having a
 * second clock in it.
 *
 * Two rows rather than one, because the two clocks are answerable separately:
 * a first response can be on time while the resolution is not.
 */
export default function SlaPanel({ ticket, now }: SlaPanelProps) {
  const clock = useContext(SLAClockContext)
  const at = now ?? clock ?? new Date()
  const sla = ticket.sla

  // No policy matched at creation. Saying so beats an empty panel, which reads
  // as a rendering fault rather than as an absence.
  if (!sla) {
    return (
      <SectionCard heading={getTranslate('Service level')} level="h3" quiet>
        <Typography variant="body2" color="textSecondary">
          {getTranslate('No service level applies to this ticket.')}
        </Typography>
      </SectionCard>
    )
  }

  const urgency = slaUrgency(ticket, at)
  // 'none' covers two different situations and they must not read alike: a
  // RESOLVED or CLOSED ticket has deadlines that have STOPPED applying, which
  // is not the same as one with nothing to say.
  const finished = ticket.state === 'RESOLVED' || ticket.state === 'CLOSED'
  // The third thing 'none' covers. slaUrgency returns it for an unparseable
  // respondBy too, and without this that case reported "On track" — asserting
  // a deadline was being met while the cell beside it showed an unparsed
  // string. Not knowing and being fine are different answers.
  const deadlineUnreadable =
    !finished && Number.isNaN(new Date(sla.respondBy).getTime())

  const statusLabel = () => {
    if (finished) return getTranslate('Not applicable')
    if (deadlineUnreadable) return getTranslate('Unknown')
    if (urgency === 'breached') return getTranslate('Breached')
    if (urgency === 'due-soon') return getTranslate('Due soon')
    return getTranslate('On track')
  }

  const paused = durationParts(sla.pausedSeconds || 0)
  // Computed once each, beside the other derived values, rather than twice in
  // the JSX — once to decide whether to render and once to render. Pure and
  // cheap either way, so this is readability, not performance: naming them puts
  // the "is there one" and the "what is it" in the same place, where they
  // cannot drift into asking different questions.
  const respondPromise = promisedWithin(sla.respondAfterSeconds)
  const resolvePromise = promisedWithin(sla.resolveAfterSeconds)
  const policyLabel = slaPolicyLabel(sla)

  /**
   * How much of the first-response window has been consumed, 0-100.
   *
   * `null` rather than 0 whenever the number would be a fiction — a stopped
   * clock, an unparseable deadline, or a window of zero or negative length. A
   * bar drawn from a NaN renders at zero, which reads as "plenty of time" for
   * exactly the ticket whose deadline could not be read.
   *
   * Measured from createdAt to respondBy, and pause needs no special handling
   * here: the service REBASES respondBy when a ticket resumes, adding the
   * banked pause to the deadline. So the denominator has already grown by the
   * paused time and the elapsed fraction stays honest.
   */
  const elapsed = (() => {
    if (finished || deadlineUnreadable) return null
    const start = new Date(ticket.createdAt).getTime()
    const end = new Date(sla.respondBy).getTime()
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null
    const fraction = (at.getTime() - start) / (end - start)
    return Math.max(0, Math.min(100, fraction * 100))
  })()

  // h3, like every other panel in this rail. The level was h2 on the argument
  // that this panel is a SIBLING of "Ticket properties" — but it renders inside
  // the <aside> that heading labels, so the DOM said child while the outline
  // said sibling. The half of that note which was right is now structural:
  // SectionCard requires a level, so the h6 that subtitle2 maps to on its own
  // is no longer reachable by omission.
  return (
    <SectionCard
      heading={getTranslate('Service level')}
      level="h3"
      headingId={HEADING_ID}
      quiet>
      <TableContainer>
        {/* Labelled BY the heading rather than carrying its own copy of the
            same string, so the two cannot drift apart. */}
        <Table size="small" aria-labelledby={HEADING_ID}>
          <TableHead>
            <TableRow>
              <TableCell>{getTranslate('Target')}</TableCell>
              <TableCell>{getTranslate('Deadline')}</TableCell>
              <TableCell>{getTranslate('Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {/* Row headers, not cells. As plain cells a screen reader
                  announces "Deadline, ⟨date⟩" with no indication of WHICH
                  deadline — the one thing the row exists to say. */}
              <TableCell component="th" scope="row">
                {getTranslate('First response')}
                {/* The PROMISE, under the target it belongs to.
                    "Resolution 11/09/2026, 15:50" is the same string whether
                    four hours were committed or ten days, so the deadline alone
                    could not be judged — an agent could not tell a generous SLA
                    from one already stretched. Absent when the policy version
                    has been pruned; the deadline is still correct and still
                    shown. */}
                {respondPromise && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="p">
                    {respondPromise}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {/* Bare bdi, not dir="ltr". formatWhen returns a Persian
                    calendar string under /fa, containing strong-RTL characters
                    like the Persian comma and meridiem — forcing LTR reverses
                    exactly the parts that are directional. dir="ltr" is for
                    genuinely Latin machine strings, which a localised date is
                    not. */}
                <bdi>{formatWhen(sla.respondBy)}</bdi>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={statusLabel()}
                  color={slaTone(urgency)}
                />
                {/* The chip says the verdict; the bar says how close. A reader
                    scanning the rail gets "how much of this window is gone"
                    without reading either date.

                    aria-hidden, and the role is dropped with it: the chip and
                    the deadline beside it already carry the same fact in text,
                    so an announced progressbar would be a third reading of one
                    value. Decoration that repeats is noise, not redundancy.

                    Suppressed when the clock has stopped or the deadline is
                    unreadable — a bar drawn from a NaN is a bar at zero, which
                    reads as "plenty of time left" for a ticket whose deadline
                    could not be parsed. */}
                {elapsed !== null && (
                  <LinearProgress
                    aria-hidden="true"
                    variant="determinate"
                    value={elapsed}
                    color={urgency === 'breached' ? 'secondary' : 'primary'}
                    style={{ marginTop: 4 }}
                  />
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                {getTranslate('Resolution')}
                {resolvePromise && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="p">
                    {resolvePromise}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <bdi>{formatWhen(sla.resolveBy)}</bdi>
              </TableCell>
              {/* Empty, with the explanation once below the table.
                  "Not tracked separately" is not an absence, it is a CONSTANT
                  — it renders identically on every ticket forever, because
                  slaUrgency derives from respondBy alone and breachedAt is one
                  server-set flag for the ticket rather than one per deadline.
                  So half of a two-row table was permanently inert, and a reader
                  who learns half a table never changes stops reading the table.

                  Still not an em dash: formatWhen already emits that glyph for
                  "no deadline recorded", and one character carrying two
                  meanings in one small table is the ambiguity the previous note
                  was right to refuse. Empty cell, footnote below. */}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Said once, under the table, rather than once per row forever. */}
      <Typography variant="caption" color="textSecondary" component="p">
        {getTranslate(
          'Only the first response is tracked against a status; the resolution deadline is shown for reference.'
        )}
      </Typography>

      {/* Shown for a finished ticket too. The status reads "Not applicable"
          because the clock has stopped, and that a breach HAPPENED is a
          historical fact the stopped clock does not erase. */}
      {sla.breachedAt && (
        <Typography variant="body2" color="textSecondary">
          {getTranslate('Breach recorded')} <When iso={sla.breachedAt} />
        </Typography>
      )}

      {/* Only when it has actually been paused. A "paused for 0 s" line on
          every ticket is noise that trains the reader to skip the block.

          Only the NUMERAL is isolated: the unit is a translated noun, and
          Persian orders "۲ ساعت" with the numeral trailing the noun visually.
          Forcing the pair to LTR reverses that against the surrounding text. */}
      {paused && (
        <Typography variant="body2" color="textSecondary">
          {getTranslate('Clock paused for')} <bdi dir="ltr">{paused.value}</bdi>{' '}
          {getTranslate(paused.unit)}
        </Typography>
      )}

      {/* Which policy produced these deadlines. An agent disputing a deadline
          needs the policy and version to look it up; without them the numbers
          are unarguable in the wrong sense. Latin machine identifiers, so
          dir="ltr" is right here where it is wrong on the dates above.

          overflowWrap, because the id is a ~60-character unbroken token and
          the default `normal` will not break one. The grid tracks use
          minmax(0, 1fr) and both items set minWidth: 0, which stops the TRACK
          blowing out — but the content still ran past the card's edge and off
          screen. Same declaration as DiagnosticContextPanel's payload block,
          which had the problem first.

          caption, not body2: this is audit-trail material, not triage
          material, and at body2 it was competing with the deadlines it
          annotates. It stays visible rather than moving behind a tooltip
          because the documented need is to look it up and quote it, and
          tooltip text cannot be selected. */}
      <Typography
        variant="caption"
        color="textSecondary"
        component="p"
        style={{ overflowWrap: 'anywhere' }}>
        {getTranslate('Policy')}{' '}
        {/* The label leads, the id follows.
            This line read `Policy tok_YsKL-bLh3magkoG6CgKal… · v2` — sixty
            characters that identify the policy to a database and to nobody
            else. SLAPolicyVersion has no name field, so the label is composed
            from the dimensions that actually distinguish one policy from
            another, and composed HERE because they arrive as dimensions
            precisely so this panel can translate them.

            The id stays. The note above it is still true — an agent disputing
            a deadline needs it to look the policy up and quote it — but it is
            no longer the only thing on the line, and it no longer leads. */}
        {policyLabel && <>{policyLabel} · </>}
        <bdi dir="ltr">
          {sla.policyId} · v{sla.policyVersion}
        </bdi>
      </Typography>
    </SectionCard>
  )
}
